import { Patient } from "../../../utils/api-objects/Patient";
import { ExpertRole, SurveyType } from "../../../utils/Enums";
import { getFormattedDateWithInput } from "../../../utils/Functions";
import { filterAppointmentsByRole } from "../utils/filterAppointments";
import { getCheckupSurveyCount } from "../utils/patientStats";
import { useGetAppointmentsByEmail } from "./useGetAppointmentsByEmail";
import { useGetBloodTracker } from "./useGetBloodTracker";
import { useGetConsultsLeft } from "./useGetConsultsLeft";
import { useGetDnaTracker } from "./useGetDnaTracker";
import { useGetEvents } from "./useGetEvents";
import { useGetHealthPlans } from "./useGetHealthPlans";
import { useGetMealPlans } from "./useGetMealPlans";
import { useGetStatsByEmail } from "./useGetStatsByEmail";
import { useGetSurveyData } from "./useGetSurveyData";

type SectionedStats = {
    title: string;
    items: { label: string; value: string | number }[];
}[];

export const usePatientsStats = (patient: Patient | null) => {
    const { stats, isLoading: isStatsLoading, error: statsError } = useGetStatsByEmail(patient?.email || null)
    const { events, isLoading: isEventsLoading, error: eventsError } = useGetEvents(patient?.user_id || null, ["login_success", "login_failed"])
    const { lastSurveyUpdate, surveysCompleted, isLoading: isHealthAssessmentLoading } = useGetSurveyData(patient?.user_id || null)
    const { appointments, isLoading: isAppointmentsLoading, error: appointmentsError } = useGetAppointmentsByEmail(patient?.email || null)
    const { dnaTracker, dnaPickupDate, isLoading: isDnaLoading } = useGetDnaTracker(patient?.user_id || null)
    const { bloodTracker, bloodPickupDate, isLoading: isBloodLoading } = useGetBloodTracker(patient?.user_id || null)
    const { mealPlans, isLoading: isMealPlanLoading } = useGetMealPlans(patient?.user_id || null)
    const { healthPlans, isLoading: isHealthPlanLoading } = useGetHealthPlans(patient?.user_id || null)
    const { specialistConsultsLeft, nutritionistConsultsLeft, error: getConsultsError, isLoading: isGetConsultsLoading } = useGetConsultsLeft(patient?.user_id || null)

    if (!patient) return []

    const elvisAppointments = filterAppointmentsByRole(ExpertRole.Specialist, appointments);
    const physicianAppointments = filterAppointmentsByRole(ExpertRole.Physician, appointments);
    const nutritionistAppointments = filterAppointmentsByRole(ExpertRole.Nutritionist, appointments);

    const sectionedStats = [
        {
            title: "General",
            items: [
                { label: "Data registrazione", value: getFormattedDateWithInput(patient.created_at) },
                { label: "Numero login success", value: events.filter(e => e.event_type === "login_success")[0]?.count || 0 },
                { label: "Numero login failed", value: events.filter(e => e.event_type === "login_failed")[0]?.count || 0 },
                {
                    label: "Data richiesta ritiro DNA",
                    value: dnaPickupDate || "Mai"
                },
                {
                    label: "Data richiesta ritiro blood",
                    value: bloodPickupDate || "Mai"
                },
            ]
        },
        {
            title: "Health Assessment",
            items: [
                {
                    label: "Data Health assessment",
                    value: (surveysCompleted && (surveysCompleted.filter(s => s.survey_type === SurveyType.HealthAssessment)).length) ? getFormattedDateWithInput(surveysCompleted.filter(s => s.survey_type === SurveyType.HealthAssessment)[0].created_at) : "Mai",
                },
                {
                    label: "Numero check up survey fatti",
                    value: getCheckupSurveyCount(surveysCompleted)
                }
            ]
        },
        {
            title: "Appointments",
            items: [
                {
                    label: "Primo consulto con Elvis",
                    value: elvisAppointments.length > 0
                        ? getFormattedDateWithInput(elvisAppointments[0].date)
                        : "Mai"
                },
                {
                    label: "Ultimo consulto con Elvis",
                    value: elvisAppointments.length > 0
                        ? getFormattedDateWithInput(elvisAppointments[elvisAppointments.length - 1].date)
                        : "Mai"
                },
                {
                    label: "Consulti con Elvis fatti",
                    value: elvisAppointments.length
                },
                {
                    label: "Primo consulto con nutri",
                    value: nutritionistAppointments.length > 0
                        ? getFormattedDateWithInput(nutritionistAppointments[0].date)
                        : "Mai"
                },
                {
                    label: "Ultimo consulto con nutri",
                    value: nutritionistAppointments.length > 0
                        ? getFormattedDateWithInput(nutritionistAppointments[nutritionistAppointments.length - 1].date)
                        : "Mai"
                },
                {
                    label: "Consulti con nutri fatti",
                    value: nutritionistAppointments.length
                },
                {
                    label: "Primo consulto con endocrinologo",
                    value: physicianAppointments.length > 0
                        ? getFormattedDateWithInput(physicianAppointments[0].date)
                        : "Mai"
                },
                {
                    label: "Ultimo consulto con endocrinologo",
                    value: physicianAppointments.length > 0
                        ? getFormattedDateWithInput(physicianAppointments[physicianAppointments.length - 1].date)
                        : "Mai"
                },
                {
                    label: "Consulti con endocrinologo fatti",
                    value: physicianAppointments.length
                },
                {
                    label: "Consulti con nutri rimasti",
                    value: `${nutritionistConsultsLeft}`
                },
                {
                    label: "Consulti con specialist (Elvis / endocrinologo) rimasti",
                    value: `${specialistConsultsLeft}`
                }
            ]
        },
        {
            title: "Plans",
            items: [
                {
                    label: "Primo MP deployed",
                    value: mealPlans?.length ? getFormattedDateWithInput(mealPlans[mealPlans.length - 1].created_at) : "Mai"
                },
                {
                    label: "Ultimo MP deployed",
                    value: mealPlans?.length ? getFormattedDateWithInput(mealPlans[0].created_at) : "Mai"
                },
                {
                    label: "Numero MP deployed",
                    value: mealPlans?.length || "Mai"
                },
                {
                    label: "Primo HP deployed",
                    value: healthPlans?.length ? getFormattedDateWithInput(healthPlans[healthPlans.length - 1].created_at) : "Mai"
                },
                {
                    label: "Ultimo HP deployed",
                    value: healthPlans?.length ? getFormattedDateWithInput(healthPlans[0].created_at) : "Mai"
                },
                {
                    label: "Numero HP deployed",
                    value: healthPlans?.length || "Mai"
                }
            ]
        }
    ];

    return sectionedStats
}